import { useState, useEffect } from 'react'
import { getFileTypeInfo, truncateFilename } from '../utils/fileHelpers'
import { TEXT_EXTENSIONS, EXCEL_EXTENSIONS } from '../utils/constants'

const SFXmark = ({ size = 20 }) => (<svg width={size} height={size} viewBox="0 0 10 10" fill="currentColor"><path d="M9.19336 8.45508C9.39258 8.6543 9.39258 8.98828 9.19336 9.19336C8.99414 9.39844 8.64844 9.39258 8.44922 9.19336L4.69922 5.44336L0.949219 9.19336C0.75 9.39258 0.410156 9.39258 0.205078 9.19336C0 8.99414 0.00585938 8.6543 0.205078 8.45508L3.95508 4.69922L0.205078 0.949219C0.00585938 0.75 0.00585938 0.416016 0.205078 0.210938C0.404297 0 0.75 0.00585938 0.949219 0.210938L4.69922 3.96094L8.44922 0.210938C8.6543 0 8.98828 0.00585938 9.19336 0.210938C9.39844 0.410156 9.39258 0.75 9.19336 0.949219L5.44336 4.69922L9.19336 8.45508Z"/></svg>)
const SFCheckmark = ({ size = 20 }) => (<svg width={size} height={size} viewBox="0 0 10 10" fill="currentColor"><path d="M3.96094 10.166C3.66797 10.166 3.45117 10.043 3.24609 9.79102L0.210938 5.99414C0.0644531 5.8125 0 5.64258 0 5.4668C0 5.0625 0.298828 4.76953 0.708984 4.76953C0.966797 4.76953 1.14258 4.86328 1.3125 5.0918L3.9375 8.46094L9.0293 0.375C9.20508 0.0996094 9.375 0 9.66211 0C10.0723 0 10.3535 0.28125 10.3535 0.685547C10.3535 0.84375 10.3066 1.00781 10.1836 1.19531L4.67578 9.78516C4.50586 10.0371 4.27148 10.166 3.96094 10.166Z"/></svg>)

function AttachmentPreview({ files, initialIndex, onClose, onRemoveFile }) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex || 0)
  const [objUrls, setObjUrls] = useState([])
  const [textContents, setTextContents] = useState({})
  const [htmlContents, setHtmlContents] = useState({})
  const [pdfPages, setPdfPages] = useState({})
  const [mammothLoaded, setMammothLoaded] = useState(false)
  const [pdfjsLoaded, setPdfjsLoaded] = useState(false)
  const [xlsxLoaded, setXlsxLoaded] = useState(false)

  useEffect(() => {
    if (window.mammoth) setMammothLoaded(true)
    else { const s = document.createElement('script'); s.src = 'https://cdnjs.cloudflare.com/ajax/libs/mammoth/1.6.0/mammoth.browser.min.js'; s.onload = () => setMammothLoaded(true); document.head.appendChild(s) }
    if (window.pdfjsLib) setPdfjsLoaded(true)
    else { const s = document.createElement('script'); s.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js'; s.onload = () => { window.pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js'; setPdfjsLoaded(true) }; document.head.appendChild(s) }
    if (window.XLSX) setXlsxLoaded(true)
    else { const s = document.createElement('script'); s.src = 'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js'; s.onload = () => setXlsxLoaded(true); document.head.appendChild(s) }
  }, [])

  useEffect(() => {
    if (!files?.length) return
    const urls = files.map(f => URL.createObjectURL(f))
    setObjUrls(urls)
    files.forEach((f, i) => {
      const ext = f.name?.split('.').pop()?.toLowerCase()
      if (TEXT_EXTENSIONS.includes(ext)) {
        const reader = new FileReader()
        reader.onload = (e) => setTextContents(prev => ({ ...prev, [i]: e.target.result }))
        reader.readAsText(f)
      }
    })
    return () => urls.forEach(u => URL.revokeObjectURL(u))
  }, [files])

  useEffect(() => {
    if (!mammothLoaded || !files?.length) return
    files.forEach((f, i) => {
      const ext = f.name?.split('.').pop()?.toLowerCase()
      if (ext === 'docx' && window.mammoth && !htmlContents[i]) {
        const reader = new FileReader()
        reader.onload = (e) => { window.mammoth.convertToHtml({ arrayBuffer: e.target.result }).then(result => { if (result.value) setHtmlContents(prev => ({ ...prev, [i]: result.value })) }).catch(() => {}) }
        reader.readAsArrayBuffer(f)
      }
    })
  }, [mammothLoaded, files])

  useEffect(() => {
    if (!pdfjsLoaded || !files?.length) return
    files.forEach((f, i) => {
      const ext = f.name?.split('.').pop()?.toLowerCase()
      const isPdf = ext === 'pdf' || f.type === 'application/pdf'
      if (isPdf && window.pdfjsLib && !pdfPages[i]) {
        const reader = new FileReader()
        reader.onload = async (e) => {
          try {
            const pdf = await window.pdfjsLib.getDocument({ data: e.target.result }).promise
            const pages = []
            for (let p = 1; p <= pdf.numPages; p++) {
              const page = await pdf.getPage(p); const scale = 2; const viewport = page.getViewport({ scale })
              const canvas = document.createElement('canvas'); canvas.width = viewport.width; canvas.height = viewport.height
              await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise
              pages.push(canvas.toDataURL('image/jpeg', 0.85))
            }
            setPdfPages(prev => ({ ...prev, [i]: pages }))
          } catch {}
        }
        reader.readAsArrayBuffer(f)
      }
    })
  }, [pdfjsLoaded, files])

  useEffect(() => {
    if (!xlsxLoaded || !files?.length) return
    files.forEach((f, i) => {
      const ext = f.name?.split('.').pop()?.toLowerCase()
      if (EXCEL_EXTENSIONS.includes(ext) && window.XLSX && !htmlContents[i]) {
        const reader = new FileReader()
        reader.onload = (e) => { try { const wb = window.XLSX.read(e.target.result, { type: 'array' }); const ws = wb.Sheets[wb.SheetNames[0]]; setHtmlContents(prev => ({ ...prev, [i]: window.XLSX.utils.sheet_to_html(ws, { editable: false }) })) } catch {} }
        reader.readAsArrayBuffer(f)
      }
    })
  }, [xlsxLoaded, files])

  if (!files?.length || !objUrls.length) return null
  const file = files[currentIndex]; const objUrl = objUrls[currentIndex]
  if (!file || !objUrl) return null

  const isMediaFile = (f) => f.type.startsWith('image/') || f.type.startsWith('video/')
  const showFilename = !isMediaFile(file)

  const handleCancel = () => {
    if (onRemoveFile) {
      const removingIndex = currentIndex
      if (files.length <= 1) {
        onRemoveFile(removingIndex)
        onClose()
      } else {
        const newIndex = removingIndex === files.length - 1 ? removingIndex - 1 : removingIndex
        setCurrentIndex(newIndex)
        onRemoveFile(removingIndex)
      }
    } else {
      onClose()
    }
  }

  const renderFileContent = (f, url, idx) => {
    const fIsImg = f.type.startsWith('image/'); const fIsVid = f.type.startsWith('video/')
    const fIsPdf = f.type === 'application/pdf' || f.name?.toLowerCase().endsWith('.pdf')
    const ext = f.name?.split('.').pop()?.toLowerCase()
    const isOffice = ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(ext)
    const isText = textContents[idx] !== undefined; const hasHtml = htmlContents[idx] !== undefined
    const fTypeInfo = getFileTypeInfo(f.name, f.type)
    const isExcel = EXCEL_EXTENSIONS.includes(ext)

    if (fIsImg) return <img src={url} alt={f.name} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: 'var(--radius-md)' }} />
    if (fIsVid) return <video src={url} controls autoPlay style={{ maxWidth: '100%', maxHeight: '100%', borderRadius: 'var(--radius-md)' }} />
    if (fIsPdf && pdfPages[idx]) return (
      <div style={{ width: '100%', height: '100%', overflowY: 'auto', WebkitOverflowScrolling: 'touch', background: 'var(--color-bg-app)', padding: '8px 4px' }}>
        <div style={{ width: '100%', maxWidth: '540px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {pdfPages[idx].map((pageImg, pi) => (
            <div key={pi} style={{ position: 'relative' }}>
              <img src={pageImg} alt={`Page ${pi + 1}`} style={{ width: '100%', display: 'block', borderRadius: pi === 0 ? '12px 12px 0 0' : pi === pdfPages[idx].length - 1 ? '0 0 12px 12px' : '0' }} />
              <div style={{ position: 'absolute', top: '8px', left: '10px', background: 'var(--overlay-light)', borderRadius: 'var(--radius-sm)', padding: '3px 8px', fontSize: 'var(--text-xs)', color: 'var(--color-text-primary)', fontWeight: 'var(--weight-medium)' }}>{pi + 1} of {pdfPages[idx].length}</div>
            </div>
          ))}
        </div>
      </div>
    )
    if (fIsPdf) return (
      <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '16px' }}>
        <div style={{ width: '80px', height: '80px', borderRadius: 'var(--radius-lg)', background: 'var(--doc-pdf)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><span style={{ fontSize: '36px', fontWeight: 'var(--weight-bold)', color: 'var(--color-text-primary)' }}>P</span></div>
        <p style={{ fontSize: 'var(--text-lg)', color: 'var(--color-text-primary)', margin: 0 }}>{f.name}</p>
        <div style={{ width: '24px', height: '24px', border: '2px solid var(--color-text-secondary)', borderTop: '2px solid transparent', borderRadius: 'var(--radius-circle)', animation: 'pulse 1s linear infinite' }} />
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>Rendering pages...</p>
      </div>
    )
    if (isText) return (
      <div style={{ width: '100%', height: '100%', overflow: 'auto', background: 'var(--color-bg-card-heavy)', borderRadius: 'var(--radius-md)', padding: '16px' }}>
        <pre style={{ color: '#e0e0e0', fontSize: 'var(--text-sm)', fontFamily: 'var(--font-mono)', whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0, lineHeight: 'var(--leading-relaxed)' }}>{textContents[idx]}</pre>
      </div>
    )
    if (isOffice && hasHtml) {
      const docStyles = `.doc-preview-content { color: #1a1a1a; font-size: ${isExcel ? '12px' : '14px'}; line-height: 1.65; font-family: ${isExcel ? 'var(--font-system)' : 'var(--font-serif)'}; } .doc-preview-content p { margin: 0 0 8px; } .doc-preview-content h1 { font-size: 20px; font-weight: 700; margin: 16px 0 8px; font-family: var(--font-system); } .doc-preview-content h2 { font-size: 17px; font-weight: 700; margin: 14px 0 6px; font-family: var(--font-system); } .doc-preview-content table { border-collapse: collapse; width: max-content; min-width: 100%; font-size: 11px; } .doc-preview-content td, .doc-preview-content th { border: 1px solid #d0d0d0; padding: ${isExcel ? '5px 8px' : '6px 8px'}; text-align: left; vertical-align: top; white-space: nowrap; } .doc-preview-content th, .doc-preview-content tr:first-child td { background: #f0f0f0; font-weight: 600; }`
      return (
        <div style={{ width: '100%', height: '100%', overflowY: 'auto', WebkitOverflowScrolling: 'touch', background: 'var(--color-bg-app)', padding: '8px 4px' }}>
          <div style={{ width: '100%', maxWidth: '540px', margin: '0 auto', background: '#fff', borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-doc)', overflow: isExcel ? 'auto' : 'hidden' }}>
            <div style={{ padding: isExcel ? '12px 8px 20px' : '20px 16px 32px', overflowX: isExcel ? 'auto' : 'visible' }}>
              <style>{docStyles}</style>
              <div className="doc-preview-content" dangerouslySetInnerHTML={{ __html: htmlContents[idx] }} />
            </div>
          </div>
        </div>
      )
    }
    return (
      <div style={{ textAlign: 'center' }}>
        <div style={{ width: '100px', height: '100px', borderRadius: 'var(--radius-card)', background: fTypeInfo.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
          <span style={{ fontSize: '48px', fontWeight: 'var(--weight-bold)', color: 'var(--color-text-primary)' }}>{fTypeInfo.letter}</span>
        </div>
        <p style={{ fontSize: 'var(--text-lg)', color: 'var(--color-text-primary)', marginBottom: '4px' }}>{f.name}</p>
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>{fTypeInfo.label} — {(f.size / 1024).toFixed(0)} KB</p>
      </div>
    )
  }

  return (
    <div className="preview-overlay" onClick={onClose}>
      <div style={{ padding: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }} onClick={e => e.stopPropagation()}>
        {/* X (cancel) — removes the currently previewed file */}
        <button onClick={handleCancel} style={{ width: '44px', height: '44px', borderRadius: '22px', background: 'var(--overlay-btn)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-text-primary)' }}>
          <SFXmark size={18} />
        </button>

        {showFilename ? (
          <p style={{ fontSize: 'var(--text-md)', fontWeight: 'var(--weight-semibold)', color: 'var(--color-text-primary)', margin: 0, flex: 1, textAlign: 'center', padding: '0 12px' }}>{truncateFilename(file.name, 30)}</p>
        ) : (
          <div style={{ flex: 1 }} />
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {files.length > 1 && <span style={{ color: 'var(--color-text-tertiary)', fontSize: '14px', fontWeight: 'var(--weight-medium)' }}>{currentIndex + 1}/{files.length}</span>}
          {/* Tick (confirm) — blue liquid glass, just closes preview */}
          <button onClick={onClose} style={{
            width: '44px', height: '44px', borderRadius: '22px',
            background: 'rgb(48, 134, 255)',
            backdropFilter: 'saturate(180%) blur(20px)',
            WebkitBackdropFilter: 'saturate(180%) blur(20px)',
            border: '0.5px solid rgba(255, 255, 255, 0.25)',
          
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', padding: 0,
          }}>
            <SFCheckmark size={16} />
          </button>
        </div>
      </div>
      {files.length > 1 ? (
        <div className="gallery-swipe no-sb" ref={el => { if (el) el.scrollLeft = currentIndex * el.offsetWidth }}
          onScroll={e => { const idx = Math.round(e.target.scrollLeft / e.target.offsetWidth); if (idx !== currentIndex && idx >= 0 && idx < files.length) setCurrentIndex(idx) }}
          style={{ flex: 1 }} onClick={e => e.stopPropagation()}>
          {files.map((f, i) => (<div key={i} className="gallery-swipe-item" style={{ padding: '16px' }}>{objUrls[i] ? renderFileContent(f, objUrls[i], i) : null}</div>))}
        </div>
      ) : (
        <div style={{ flex: 1, display: 'flex', alignItems: 'stretch', justifyContent: 'center', overflow: 'auto' }} onClick={e => e.stopPropagation()}>{renderFileContent(file, objUrl, currentIndex)}</div>
      )}
      {files.length > 1 && (
        <div className="no-sb" style={{ padding: '10px 16px', display: 'flex', gap: '6px', overflowX: 'auto', background: 'rgba(0,0,0,0.7)', flexShrink: 0 }} onClick={e => e.stopPropagation()}>
          {files.map((f, i) => {
            const fIsImg = f.type.startsWith('image/'); const fIsVid = f.type.startsWith('video/'); const fTypeInfo = getFileTypeInfo(f.name, f.type)
            return (
              <div key={i} onClick={() => { setCurrentIndex(i); const el = document.querySelector('.preview-overlay .gallery-swipe'); if (el) el.scrollTo({ left: i * el.offsetWidth, behavior: 'smooth' }) }}
                style={{ width: '52px', height: '52px', borderRadius: 'var(--radius-sm)', overflow: 'hidden', cursor: 'pointer', border: i === currentIndex ? 'var(--border-gallery-active)' : 'var(--border-gallery-inactive)', opacity: i === currentIndex ? 1 : 0.55, flexShrink: 0, transition: 'opacity 0.2s, border 0.2s', background: fIsImg || fIsVid ? 'var(--color-bg-app)' : fTypeInfo.bg }}>
                {fIsImg && objUrls[i] ? <img src={objUrls[i]} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> :
                 fIsVid && objUrls[i] ? <video src={objUrls[i]} style={{ width: '100%', height: '100%', objectFit: 'cover' }} muted /> :
                 <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><span style={{ fontSize: '18px', fontWeight: 'var(--weight-bold)', color: 'var(--color-text-primary)' }}>{fTypeInfo.letter}</span></div>}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default AttachmentPreview